import { useMemo } from 'react';

import { AddIcon, Button, type TypeSelectOption } from '@thefarmersfront/kitchen';

import { Table, TableBottomField, TableTopField } from '~/components/common';

import { useStableCallback } from '~/hooks/common';
import { useUserInfoStore } from '~/store';
import { getClaimReturnColumnDefs } from '~/utils/claim';

import { CLAIM_PAGE_SIZE } from '~/constants/claim';

import type { TypeReturnData } from '~/types/claim.types';
import type { TypePagination } from '~/types/common.types';

import styles from './ClaimReturnTable.module.scss';

const ClaimReturnTable = ({
  data,
  pagination,
  selectedItem,
  onChangePageLimit,
  onChangePage,
  onChangeRowSelection,
  onClickCancelReturn,
  onOpenClaimReturnModal,
  onClickReturnReceiptId,
}: {
  data: TypeReturnData[] | null;
  pagination: TypePagination;
  selectedItem: TypeReturnData | null;
  onChangePageLimit: (value: TypeSelectOption<number>) => void;
  onChangePage: (value: number) => void;
  onChangeRowSelection: (selectedRows: TypeReturnData[]) => void;
  onClickCancelReturn: () => void;
  onOpenClaimReturnModal: () => void;
  onClickReturnReceiptId: (returnReceiptId: string) => void;
}) => {
  const hasPermission = useUserInfoStore((state) => state.hasPermission);
  const hasPermission3PL = hasPermission([{ ownerType: '3PL' }]);

  const handleClickReturnReceiptId = useStableCallback(onClickReturnReceiptId);
  const { columnDefs } = useMemo(
    () => getClaimReturnColumnDefs({ hasPermission3PL, onClickReturnReceiptId: handleClickReturnReceiptId }),
    [hasPermission3PL, handleClickReturnReceiptId]
  );

  return (
    <div className={styles['table-container']}>
      <TableTopField
        dataTotalCount={pagination.totalCount}
        rightButton={
          hasPermission3PL ? (
            <div className={styles['table-top-right-field']}>
              <Button
                size="tiny"
                variant="outlined"
                status="error"
                disabled={!selectedItem}
                onClick={onClickCancelReturn}
              >
                반품 취소
              </Button>
              <Button size="tiny" color="secondary" leftIcon={<AddIcon size={16} />} onClick={onOpenClaimReturnModal}>
                반품 신청
              </Button>
            </div>
          ) : undefined
        }
      />
      <div className={styles['table-wrapper']}>
        <Table
          data={data}
          columns={columnDefs}
          rowStatusType={data === null ? 'INIT' : 'EMPTY'}
          selection={hasPermission3PL ? { mode: 'single', onChange: onChangeRowSelection } : undefined}
        />
      </div>
      <TableBottomField
        pageSizeOptions={Object.values(CLAIM_PAGE_SIZE)}
        currentPage={pagination.currentPage}
        totalCount={pagination.totalCount}
        pageSize={pagination.pageSize}
        onChangePageLimit={onChangePageLimit}
        onClickPage={onChangePage}
      />
    </div>
  );
};

export default ClaimReturnTable;
